import { colors, gridSize } from '@atlaskit/theme';
import React from 'react';
import { useIntl } from 'react-intl';
import styled from '@emotion/styled';
import appleLogo from '../images/apple-logo.svg';
import googleLogo from '../images/google-logo.svg';
import microsoftLogo from '../images/microsoft-logo.svg';
import slackLogo from '../images/slack-logo.svg';

import {
  isGoogleLoginOutageAdvisoryEnabled,
  isMicrosoftLoginOutageAdvisoryEnabled,
  isAppleLoginOutageAdvisoryEnabled,
  isSlackLoginOutageAdvisoryEnabled,
} from '../utilities/social-login';
import messages from './SocialLoginPanel.messages';
import TallButton, { TallButtonProps } from './TallButton';
import SectionMessage from '@atlaskit/section-message';
import WarningIcon from '@atlaskit/icon/glyph/warning';
import Tooltip from '@atlaskit/tooltip';
import { useDynamicConfigs } from '../utilities/feature-flags/dynamic-config';
import { EventAttributes } from '../utilities/analytics/analytics-web-client';

export interface Props {
  handleGoogleLogin: (analyticsAttributes?: EventAttributes) => void;
  handleMicrosoftLogin: () => void;
  handleAppleLogin: () => void;
  handleSlackLogin: () => void;
}

export const SocialLoginPanel: React.FunctionComponent<Props> = ({
  handleGoogleLogin,
  handleMicrosoftLogin,
  handleAppleLogin,
  handleSlackLogin,
}) => {
  const { formatMessage } = useIntl();
  const dynamicConfigs = useDynamicConfigs();

  const googleOutageEnabled: boolean = isGoogleLoginOutageAdvisoryEnabled(dynamicConfigs);
  const microsoftOutageEnabled: boolean = isMicrosoftLoginOutageAdvisoryEnabled(dynamicConfigs);
  const appleOutageEnabled: boolean = isAppleLoginOutageAdvisoryEnabled(dynamicConfigs);
  const slackOutageEnabled: boolean = isSlackLoginOutageAdvisoryEnabled(dynamicConfigs);

  const isSocialLoginOutage: boolean =
    googleOutageEnabled || microsoftOutageEnabled || appleOutageEnabled || slackOutageEnabled;

  const getAdvisorySectionMessage = (): React.ReactNode => {
    const multipleOutages =
      [googleOutageEnabled, microsoftOutageEnabled, appleOutageEnabled, slackOutageEnabled].filter(
        Boolean
      ).length > 1;

    const sectionMessageTitle: string = multipleOutages
      ? formatMessage(messages.someOutageTitle)
      : getAdvisorySectionMessageTitle();

    const sectionMessageDescription: string = multipleOutages
      ? formatMessage(messages.someOutageDescription)
      : getAdvisorySectionMessageDescription();

    return (
      <SectionMessage
        testId="social-login-outage-section-message"
        title={sectionMessageTitle}
        appearance="warning">
        <p>{sectionMessageDescription}</p>
      </SectionMessage>
    );
  };

  const getAdvisorySectionMessageTitle = (): string =>
    googleOutageEnabled
      ? formatMessage(messages.googleOutageTitle)
      : microsoftOutageEnabled
      ? formatMessage(messages.microsoftOutageTitle)
      : appleOutageEnabled
      ? formatMessage(messages.appleOutageTitle)
      : slackOutageEnabled
      ? formatMessage(messages.slackOutageTitle)
      : formatMessage(messages.someOutageTitle);

  const getAdvisorySectionMessageDescription = (): string =>
    googleOutageEnabled
      ? formatMessage(messages.googleOutageDescription)
      : microsoftOutageEnabled
      ? formatMessage(messages.microsoftOutageDescription)
      : appleOutageEnabled
      ? formatMessage(messages.appleOutageDescription)
      : slackOutageEnabled
      ? formatMessage(messages.slackOutageDescription)
      : formatMessage(messages.someOutageDescription);

  const handleGoogleLoginOnClick = () => {
    handleGoogleLogin();
  };

  const GoogleButton = () => (
    <SocialLoginOptionWrapper>
      <SocialLoginButton
        id="google-auth-button"
        logo={googleLogo}
        onClick={handleGoogleLoginOnClick}
        advisoryEnabled={googleOutageEnabled}
        outageTooltipMessage={formatMessage(messages.googleOutageDescription)}>
        Google
      </SocialLoginButton>
    </SocialLoginOptionWrapper>
  );

  const MicrosoftButton = () => (
    <SocialLoginOptionWrapper>
      <SocialLoginButton
        id="microsoft-auth-button"
        logo={microsoftLogo}
        onClick={handleMicrosoftLogin}
        advisoryEnabled={microsoftOutageEnabled}
        outageTooltipMessage={formatMessage(messages.microsoftOutageDescription)}>
        Microsoft
      </SocialLoginButton>
    </SocialLoginOptionWrapper>
  );

  const AppleButton = () => (
    <SocialLoginOptionWrapper>
      <SocialLoginButton
        id="apple-auth-button"
        logo={appleLogo}
        onClick={handleAppleLogin}
        advisoryEnabled={appleOutageEnabled}
        outageTooltipMessage={formatMessage(messages.appleOutageDescription)}>
        Apple
      </SocialLoginButton>
    </SocialLoginOptionWrapper>
  );

  const SlackButton = () => (
    <SocialLoginOptionWrapper>
      <SocialLoginButton
        id="slack-auth-button"
        logo={slackLogo}
        onClick={handleSlackLogin}
        advisoryEnabled={slackOutageEnabled}
        outageTooltipMessage={formatMessage(messages.slackOutageDescription)}>
        Slack
      </SocialLoginButton>
    </SocialLoginOptionWrapper>
  );

  const showButtonsGridLayout = () => (
    <>
      <SocialLoginButtonRowGroup data-testid="social-login-button-row">
        {GoogleButton()}
        {MicrosoftButton()}
      </SocialLoginButtonRowGroup>
      <SocialLoginButtonRowGroup data-testid="social-login-button-row">
        {AppleButton()}
        {SlackButton()}
      </SocialLoginButtonRowGroup>
    </>
  );

  return (
    <SocialLoginPanelWrapper
      data-i18n-or={formatMessage(messages.socialAuthOr)}
      data-i18n-continue={formatMessage(messages.socialAuthOrContinueWith)}
      className="google-login social-login"
      data-testid="social-login-wrapper">
      {showButtonsGridLayout()}
      {isSocialLoginOutage && getAdvisorySectionMessage()}
    </SocialLoginPanelWrapper>
  );
};

export const SocialLoginPanelWrapper = styled.ul`
  margin-top: ${gridSize() * 3}px;
  list-style: none;
  padding-inline: 0;
  margin-block-end: 0;
  &::before {
    color: ${colors.N300};
    content: attr(data-i18n-continue);
    display: block;
    font-size: 14px;
    line-height: 16px;
    margin-bottom: ${gridSize() * 2}px;
    font-weight: 600;
    text-align: center;
  }
`;

export const SocialLoginButtonRowGroup = styled.div`
  &:not(:last-child) {
    margin-bottom: ${gridSize() * 2}px;
  }
`;

export const SocialLoginOptionWrapper = styled.li`
  &:not(:last-child) {
    margin-bottom: ${gridSize() * 2}px;
  }
`;

export const SocialLoginButton = ({
  children,
  logo,
  advisoryEnabled,
  outageTooltipMessage,
  ...props
}: TallButtonProps & {
  logo?: string;
  advisoryEnabled: boolean;
  outageTooltipMessage?: string;
}) => {
  const commonProps = {
    isFullWidth: true,
    iconBefore: logo ? <img src={logo} alt="" /> : undefined,
  };

  if (advisoryEnabled) {
    return (
      <Tooltip testId="social-auth-button-tooltip" content={outageTooltipMessage}>
        <StyledSocialLoginButton
          advisoryEnabled={true}
          iconAfter={
            <WarningIcon
              testId="social-auth-button-warning-icon"
              label=""
              primaryColor={colors.Y500}
              secondaryColor={colors.Y50}
            />
          }
          {...commonProps}
          {...props}>
          {children}
        </StyledSocialLoginButton>
      </Tooltip>
    );
  } else {
    return (
      <StyledSocialLoginButton advisoryEnabled={false} {...commonProps} {...props}>
        {children}
      </StyledSocialLoginButton>
    );
  }
};

export const StyledSocialLoginButton = styled(TallButton, {
  shouldForwardProp: propName => !['advisoryEnabled'].includes(propName),
})<{ advisoryEnabled: boolean }>`
  background: ${props => (props.advisoryEnabled ? colors.Y50 : colors.N0)} !important;
  box-shadow: none;
  font-weight: bold;
  border: 1px solid ${colors.N50};
  border-radius: 3px;
  color: ${colors.N800};
  span {
    flex-grow: unset;
  }

  &:not(:last-child) {
    margin-bottom: ${gridSize() * 0.5}px;
  }

  &:hover {
    background: ${colors.N10A} !important;
  }
  &:active {
    background: ${colors.N20A} !important;
    color: ${colors.N400} !important;
  }
  &:focus {
    box-shadow: 1px 1px 5px 0 rgba(0, 0, 0, 0.2), 0 0 0 2px rgba(38, 132, 255, 0.6) !important;
  }
  img {
    height: 24px;
    width: 24px;
    margin-right: 6px;
  }
`;

export default SocialLoginPanel;
